import * as React from "react";
import Bacon from 'baconjs';
import {useCallback, useContext, useMemo} from "react";
import styles from './LineGroup.module.css';
import classnames from 'classnames';
import {CaptionsContext} from "../CaptionsContext";
import {newSlateEditorOps, setLineBeginning, setLineData} from "../../../models/Captions";
import {isEmpty, useBaconObservable} from "../../../utils";
import {Gutter} from "./Gutter";
import {useSelected, useSlate} from "slate-react";
import {LineGroup as LineGroupBlock} from "../../../models/Captions/types";
import {SlateEditor} from "../../../models/ICaptionsEditor";
import {getEffectiveWordTimingsForLine} from "../../Timeline/wordTiming";


type Props = {
  children: any,
  attributes: any,

  lineBlock: LineGroupBlock,
  time?: number,
  duration?: number,
  direction: 'rtl'|'ltr'
};


export function useTimeMatch(time?: number|null, duration?: number|null) {
  const context = useContext(CaptionsContext);

  // For now, just recreate the filter function, but we could also assign to ref
  const isWithinOurTimeRange = useCallback((needleTime: number) => {
    if (isEmpty(time) || isEmpty(duration)) {
      return false;
    }
    if (needleTime < time) {
      return false;
    }
    if (needleTime > time + duration) {
      return false;
    }
    return true;
  }, [time, duration]);

  const filteredObservable = useMemo(() => {
    if (!context) {
      return Bacon.never();
    }
    return context.currentTime$.map(time => isWithinOurTimeRange(time)).skipDuplicates();
  }, [context ? context.currentTime$ : null, isWithinOurTimeRange]);

  // subscribe to the observable.
  return useBaconObservable(filteredObservable, false);
}


type LineGroupContextValue = {
  lineTime: number|null|undefined,
  effectiveWordTimingsById: Map<string, {relativeTime: number, relativeDuration: number}>,
};

// Using this context, we pass through the line time and effective word timings to the vocables.
// This is needed because Slate.js would otherwise not re-render the vocable, just because the line data changed.
// An alternative would be to store the absolute timestamp on each word in the model. But then, while moving a line,
// we would need to rewrite all word timestamps. Not only is this more work, my bet here is that is React context
// alone is faster than the Slate.js system, even if we have to pay a price in terms of setup cost, that is, the
// cost of of setting up all the context bindings.
export const LineGroupContext = React.createContext<LineGroupContextValue>({
  lineTime: null,
  effectiveWordTimingsById: new Map(),
});



export function LineGroup(props: Props) {
  const context = useContext(CaptionsContext);
  const isSelected = useSelected();
  const editor = useSlate() as SlateEditor;

  const lineIndex = editor.children.findIndex(node => (node as LineGroupBlock) == props.lineBlock);
  const prevLineIdx = lineIndex === 0 ? null : lineIndex-1;

  const matchesTimestamp = useTimeMatch(props.time, props.duration);
  const speakerId = props.lineBlock.data.speakers?.[0] ?? null;
  const effectiveWordTimingsById = useMemo(() => {
    if (lineIndex < 0) {
      return new Map<string, {relativeTime: number, relativeDuration: number}>();
    }

    return new Map(
      getEffectiveWordTimingsForLine(props.lineBlock, lineIndex, {renderMinimumDuration: false}).map(word => [
        word.vocableId,
        {
          relativeTime: word.relativeTime,
          relativeDuration: word.relativeDuration,
        }
      ])
    );
  }, [lineIndex, props.lineBlock]);

  // const handleSetCurrentTimeClick = useCallback(() => {
  //   if (!context) {
  //     return;
  //   }
  //   const time = context.getCurrentTime();
  //   if (time === null) {
  //     return;
  //   }
  //
  //   const ops = newSlateEditorOps(editor);
  //   setLineBeginning(ops, lineIndex, time);
  // }, [lineIndex, editor, context ? context.getCurrentTime : null]);

  const handleSetLineData = useCallback((what: 'time'|'duration', value: number) => {
    const ops = newSlateEditorOps(editor);
    setLineBeginning(ops, lineIndex, value);
  }, [lineIndex, editor]);

  const handleSetSpeaker = useCallback((speakerId: number|null) => {
    const ops = newSlateEditorOps(editor);
    setLineData(ops, lineIndex, {
      speakers: speakerId === null ? undefined : props.lineBlock.children.map(() => speakerId)
    });
  }, [editor, lineIndex, props.lineBlock.children]);


  let uiState;
  if (context) {
    uiState = context.uiState;
  }
  else {
    uiState = null;
  }

  const {attributes, children, time, duration, direction} = props;

  let showTimingMarkerForStart = false;
  let showTimingMarkerForEnd = false;
  if (uiState && uiState.isTimingMode && uiState.timingCursor) {
    const timingCursor = uiState.timingCursor!;

    if (timingCursor.isAfter) {
      // In this case, the next line should be marked, and we are the next line if the cursor is our previous!
      showTimingMarkerForStart = timingCursor.lineIdx == prevLineIdx;
      showTimingMarkerForEnd = timingCursor.lineIdx == lineIndex;
    }
    else {
      showTimingMarkerForStart = timingCursor.lineIdx == lineIndex;
      showTimingMarkerForEnd = timingCursor.lineIdx == lineIndex;
    }
  }

  return <div
    {...attributes}
    className={classnames({
      [styles.lineGroup]: true,
      [styles.rtl]: direction === 'rtl',
      [styles.matchesCurrentTime]: matchesTimestamp,
      [styles.isFocused]: isSelected
    })}
  >
      <Gutter
        direction={direction}
        time={time}
        lineIndex={lineIndex}
        duration={duration}
        speakerId={speakerId}
        showTimingMarkerForStart={showTimingMarkerForStart}
        showTimingMarkerForEnd={showTimingMarkerForEnd}
        onLineDataChanged={handleSetLineData}
        onSpeakerChanged={handleSetSpeaker}
      />
      <LineGroupContext.Provider value={{lineTime: time, effectiveWordTimingsById}}>
        <div className={styles.content}>{children}</div>
      </LineGroupContext.Provider>
  </div>
}


